"use client";

import FormTextField from "@/src/components/common/broadsheet/form-text-field";
import {
  captchaButtonSx,
  captchaFieldSx,
  captchaFrameSx,
  captchaImageStyle,
} from "@/src/components/common/broadsheet/form-kit";
import MetaLine from "@/src/components/common/broadsheet/meta-line";
import SectionHeading from "@/src/components/common/broadsheet/section-heading";
import {
  FONT_DISPLAY,
  FONT_TEXT,
  FONT_UI,
  actionSx,
  hairlineTop,
} from "@/src/components/common/broadsheet/tokens";
import SchemaWrapper from "@/src/components/common/form/SchemaWrapper";
import { Alert, Box, Button, InputAdornment, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import Panel from "@/src/components/common/broadsheet/panel";
import { journalApiBase } from "@/src/utils/journal-api-base";

const CommentComponent = ({
  journalSlug,
  entity,
  entityId,
}: {
  journalSlug: string;
  entity: string;
  entityId: string | number;
}) => {
  const apiBase = journalApiBase(journalSlug) ?? "";
  const [comments, setComments] = useState<Comment[]>([]);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [message, setMessage] = useState<FormMessage | null>(null);

  const formRef = useRef<SchemaWrapperRef>(null);
  const commentFormRef = useRef<HTMLDivElement>(null);

  const [captcha, setCaptcha] = useState<Captcha>({
    captcha: "",
    key: "",
  });

  useEffect(() => {
    getCaptcha();
    getComments();
  }, []);

  const schemaConfig = {
    author_name: {
      type: "string",
      label: "Your Name",
      required: true,
    },
    author_email: {
      type: "string",
      label: "Your Email Address",
      required: true,
      email: true,
    },
    body: {
      type: "string",
      label: "Your Comment",
      required: true,
    },
    captcha_phrase: {
      type: "string",
      label: "Captcha",
      required: true,
    },
  } as SchemaConfig;

  const getCaptcha = async () => {
    try {
      const response = await fetch(`${apiBase}/v1/app/captcha`);
      if (response.ok) {
        const captcha = await response.json();
        setCaptcha(captcha.data);
      }
    } catch (error: any) {
      console.error("Error fetching captcha:", error);
    }
  };

  const getComments = async () => {
    try {
      const response = await fetch(
        `${apiBase}/v1/journal/publish/${entity}/comment/${entityId}?include=replyTo`,
      );
      if (response.ok) {
        const comments = await response.json();
        setComments(comments.data);
      }
    } catch (error: any) {
      console.error("Error fetching comments:", error);
    }
  };

  const organizeCommentsHierarchy = (commentsArray: Comment[]) => {
    const commentMap: Record<string, Comment> = {};
    const rootComments: Comment[] = [];

    commentsArray.forEach((comment) => {
      commentMap[comment.id] = { ...comment, children: [] };
    });

    commentsArray.forEach((comment) => {
      const replyToId = comment.relationships?.replyTo?.data?.id;

      if (replyToId && commentMap[replyToId]) {
        commentMap[replyToId].children?.push(commentMap[comment.id]);
      } else {
        rootComments.push(commentMap[comment.id]);
      }
    });

    return rootComments;
  };

  const handleReplyClick = (commentId: string) => {
    setReplyTo(commentId);

    setTimeout(() => {
      const rect = commentFormRef.current?.getBoundingClientRect();
      if (rect) {
        const scrollToY = window.scrollY + rect.top - 75;
        window.scrollTo({
          top: scrollToY,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const organizedComments = organizeCommentsHierarchy(comments);

  const handleSubmit = async () => {
    if (!formRef.current) {
      return false;
    }

    const isValid = await formRef.current.validateForm();
    if (!isValid) {
      return false;
    }

    const currentValues = formRef.current.getValues();

    if (!currentValues.captcha_phrase) {
      setMessage({
        message: "Please enter the captcha text",
        severity: "error",
      });

      return;
    }

    const data = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        author_name: currentValues.author_name,
        author_email: currentValues.author_email,
        body: currentValues.body,
        captcha_phrase: currentValues.captcha_phrase,
        captcha_key: captcha.key,
      }),
    };

    if (replyTo !== null) {
      data.body = JSON.stringify({
        ...JSON.parse(data.body),
        reply_to: Number(replyTo),
      });
    }

    const response = await fetch(
      `${apiBase}/v1/journal/publish/${entity}/comment/${entityId}`,
      data,
    );

    if (response.ok) {
      setMessage({
        message: "Comment submitted successfully!",
        severity: "success",
      });

      setReplyTo(null);
      formRef.current?.reset();
      getCaptcha();
    } else {
      const res = await response.json();
      const message = Object.entries(res.data.errors).map(
        ([key, value]: [string, any]) => value,
      );
      setMessage({
        message: message[0],
        severity: "error",
      });
    }
  };

  const replyTarget = replyTo
    ? comments.find((comment) => comment.id === replyTo)
    : null;

  const CommentItem = ({
    comment,
    depth = 0,
    isChild = false,
  }: {
    comment: Comment;
    depth?: number;
    isChild?: boolean;
  }) => (
    <Box key={`comment-${comment.id}`}>
      <Box
        sx={{
          marginBottom: !isChild ? 1 : 0,
          padding: !isChild ? 1 : 0,
          border: !isChild ? "1px solid" : "none",
          borderColor: "custom.ruleHairline",
          ml: depth < 3 ? `${depth * 1.25}rem` : 0,
          backgroundColor: !isChild ? "custom.newsprintRaised" : "transparent",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 0.75,
            mt: isChild ? 0.75 : 0,
            ...(isChild ? { pt: 0.75, ...hairlineTop } : null),
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="strong"
              sx={{
                display: "block",
                fontFamily: FONT_UI,
                fontSize: "0.8125rem",
                fontWeight: 700,
                color: "custom.ink",
              }}
            >
              {comment.attributes.author_name}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT_TEXT,
                fontSize: "0.9375rem",
                lineHeight: 1.6,
                color: "text.primary",
              }}
            >
              {comment.attributes.body}
            </Typography>
            <MetaLine
              items={[new Date(comment.attributes.created_at).toLocaleString()]}
            />
          </Box>
          <Button
            variant="text"
            type="button"
            sx={captchaButtonSx}
            onClick={() => {
              handleReplyClick(comment.id);
            }}
          >
            Reply
          </Button>
        </Box>
        {comment.children && comment.children.length > 0 && (
          <Box>
            {comment.children.map((childComment) => (
              <CommentItem
                key={`child-comment-${childComment.id}`}
                comment={childComment}
                depth={depth + 1}
                isChild
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );

  return captcha?.captcha ? (
    <Box component="section" sx={{ mt: 1.25, pt: 1, ...hairlineTop }}>
      <SectionHeading title="Comments" component="h2" rule={false} />
      <Box
        sx={{
          maxWidth: { xs: "100%", md: "75%" },
          mx: "auto",
        }}
      >
        <SchemaWrapper
          ref={formRef}
          hasDefaultAction={false}
          config={schemaConfig}
          defaultValues={{}}
        >
          <Box ref={commentFormRef} sx={{ mb: 2.5 }}>
            {replyTarget && (
              <Panel
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 0.75,
                  mt: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.75,
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{
                      fontFamily: FONT_DISPLAY,
                      fontSize: "3rem",
                      lineHeight: 0.8,
                      color: "custom.kicker",
                    }}
                  >
                    &ldquo;
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: FONT_UI,
                        fontSize: "0.8125rem",
                        fontWeight: 700,
                        color: "custom.ink",
                      }}
                    >
                      {replyTarget.attributes.author_name || "Unknown User"}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_TEXT,
                        fontSize: "0.9375rem",
                        lineHeight: 1.6,
                        color: "custom.inkMuted",
                      }}
                    >
                      {replyTarget.attributes.body || ""}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  type="button"
                  aria-label="Close reply box"
                  title="Cancel reply"
                  onClick={() => setReplyTo(null)}
                  sx={captchaButtonSx}
                >
                  Cancel
                </Button>
              </Panel>
            )}
            <FormTextField
              isRequired
              name="author_name"
              label="Name"
              placeholder="Enter your name"
            />
            <FormTextField
              isRequired
              name="author_email"
              label="Email"
              type="email"
              placeholder="Enter your Email"
            />
            <FormTextField
              isRequired
              name="body"
              label="Comment"
              placeholder="Enter your comment"
              multiline
              minRows={4}
            />
            <FormTextField
              isRequired
              name="captcha_phrase"
              label="Captcha"
              placeholder="Enter captcha text"
              inputProps={{ autoComplete: "off" }}
              sx={captchaFieldSx}
              endAdornment={
                <InputAdornment position="end">
                  <Box sx={captchaFrameSx}>
                    <img
                      src={captcha.captcha}
                      alt="captcha"
                      style={captchaImageStyle}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      type="button"
                      aria-label="Refresh captcha"
                      onClick={getCaptcha}
                      sx={captchaButtonSx}
                    >
                      New code
                    </Button>
                  </Box>
                </InputAdornment>
              }
            />
            <Button
              variant="contained"
              color="primary"
              type="button"
              sx={{ ...actionSx("solid"), mt: 0.75 }}
              onClick={handleSubmit}
            >
              Submit Comment
            </Button>
            {message !== null && (
              <Alert severity={message.severity} sx={{ marginTop: 2 }}>
                <Typography variant="body2" component="p">
                  {message.message}
                </Typography>
              </Alert>
            )}
          </Box>
          <Box>
            {organizedComments.map((comment) => (
              <CommentItem key={`root-comment-${comment.id}`} comment={comment} />
            ))}
          </Box>
        </SchemaWrapper>
      </Box>
    </Box>
  ) : null;
};

export default CommentComponent;
